import { useState } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import {
  DashboardThemeProvider,
  ErrorState,
  KpiCardsSkeleton,
  PageHeader,
  SectionErrorBoundary,
} from '../Common/DashboardKit';
import { useFieldSales } from './hooks/useFieldSalesDashboard.js';
import FieldSalesFilterBar from './components/FieldSalesFilterBar.jsx';
import FieldSalesKpiCards from './components/FieldSalesKpiCards.jsx';
import FieldSalesInsights from './components/FieldSalesInsights.jsx';
import FieldSalesOverviewTab from './components/FieldSalesOverviewTab.jsx';
import FieldSalesTrendTab from './components/FieldSalesTrendTab.jsx';
import FieldSalesHqProductivityTab from './components/FieldSalesHqProductivityTab.jsx';
import FieldSalesMatrixTab from './components/FieldSalesMatrixTab.jsx';
import FieldSalesCriticalTab from './components/FieldSalesCriticalTab.jsx';
import FieldSalesVolatilityTab from './components/FieldSalesVolatilityTab.jsx';
import FieldSalesCreditNotesTab from './components/FieldSalesCreditNotesTab.jsx';
import { monthWireToUi, periodLabel } from './components/fieldSalesFormat.js';
import './FieldSalesDashboard.css';

/**
 * The {Mon} token, derived from the month(s) the backend actually served
 * (summary.months, in wire YYYYMM). This is correct even when the user picked
 * no month: the backend defaults to its latest month WITH sales and echoes it
 * here, so titles show the real month name (e.g. "Aug") rather than "Latest".
 */
function monthTokenFromSummary(summaryData) {
  const wire = Array.isArray(summaryData?.months) ? summaryData.months : [];
  const ui = wire.map(monthWireToUi).filter(Boolean);
  const label = periodLabel(ui);
  return label === '—' ? 'Latest' : label;
}

/**
 * Build the 7 tab descriptors. Subheadings interpolate {Mon} (month token) and
 * {N} (the Top/Bottom count from the HQ productivity response, default 20).
 */
function buildTabs(mon, n) {
  return [
    {
      label: 'Overview',
      queryKey: 'summary',
      subtitle: `${mon} — Target Achievement & YoY Growth`,
    },
    {
      label: 'Monthly Trend',
      queryKey: 'trend',
      subtitle: 'Apr → Mar: LY · Target · CY · Secondary & Achievement %',
    },
    {
      label: 'HQ Productivity',
      queryKey: 'hqProductivity',
      subtitle: `${mon} — Top ${n} & Bottom ${n} by PCPM/TM`,
    },
    {
      label: 'PCPM × Growth Matrix',
      queryKey: 'matrix',
      subtitle: 'Action Buckets',
    },
    {
      label: 'Critical HQ Watchlist',
      queryKey: 'critical',
      subtitle: `${mon} — PCPM/TM < 50% of division & negative YoY`,
    },
    {
      label: 'Volatility Analysis',
      queryKey: 'volatility',
      subtitle: `${mon}`,
    },
    {
      label: 'Credit Notes Analysis',
      queryKey: 'creditNotes',
      subtitle: 'Sales Leakage',
    },
  ];
}

/** Tab heading: the tab title + its subheading. */
function TabHeading({ label, subtitle }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0F1E3D' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.5 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

/**
 * Tab body. The Overview tab renders the real content from summary +
 * summary_hqs + breakdown; the other tabs remain title + subheading
 * placeholders. A failed OPTIONAL query shows a per-section error (NOT a
 * page-level blank).
 */
function TabPanel({ label, subtitle, query, mon, tmTotal }) {
  // Overview now holds ONLY the Performance Analysis section (which renders its
  // own "{Mon} — Target Achievement & YoY Growth" heading). KPI cards + Auto
  // Insights live at page level above the tabs.
  if (label === 'Overview') {
    if (query?.isError) {
      return <Alert severity="error">Failed to load the {label} section.</Alert>;
    }
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesOverviewTab summary={query?.data} />
      </Box>
    );
  }

  // Monthly Trend: the reference metric table (chart removed); owns its heading.
  if (label === 'Monthly Trend') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesTrendTab query={query} tmTotal={tmTotal} />
      </Box>
    );
  }

  // HQ Productivity: fetches its own data (so the N dropdown can refetch) and
  // owns its heading + live "Top N / Bottom N" subheading (N follows the
  // dropdown), so no page-level heading here to avoid a stale duplicate.
  if (label === 'HQ Productivity') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesHqProductivityTab mon={mon} />
      </Box>
    );
  }

  // PCPM × Growth Matrix: fetches its own data (so the quadrant click can
  // refetch /matrix?quad=…); owns its heading.
  if (label === 'PCPM × Growth Matrix') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesMatrixTab />
      </Box>
    );
  }

  // Critical HQ Watchlist: uses the shared critical query + the resolved {Mon}.
  if (label === 'Critical HQ Watchlist') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesCriticalTab query={query} mon={mon} />
      </Box>
    );
  }

  // Volatility Analysis: shared volatility query + resolved {Mon} subheading.
  if (label === 'Volatility Analysis') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesVolatilityTab query={query} mon={mon} />
      </Box>
    );
  }

  // Credit Notes Analysis: fetches its own data (so the type switch can
  // refetch /credit-notes?type=…); owns its heading.
  if (label === 'Credit Notes Analysis') {
    return (
      <Box sx={{ py: 1 }}>
        <FieldSalesCreditNotesTab />
      </Box>
    );
  }

  if (query?.isError) {
    return <Alert severity="error">Failed to load the {label} section.</Alert>;
  }
  return (
    <Box sx={{ py: 1 }}>
      <TabHeading label={label} subtitle={subtitle} />
    </Box>
  );
}

/**
 * Tab index → query key, in the SAME order as buildTabs(). Used to tell
 * useFieldSales which tab is visible so only that tab's endpoint is fetched
 * (lazy loading) instead of firing all of them at mount.
 */
const TAB_QUERY_KEYS = [
  'summary',        // 0 Overview
  'trend',          // 1 Monthly Trend
  'hqProductivity', // 2 HQ Productivity
  'matrix',         // 3 PCPM × Growth Matrix
  'critical',       // 4 Critical HQ Watchlist
  'volatility',     // 5 Volatility Analysis
  'creditNotes',    // 6 Credit Notes Analysis
];

export default function FieldSalesDashboard() {
  const [tab, setTab] = useState(0);
  const activeTab = TAB_QUERY_KEYS[tab] ?? 'summary';

  const {
    summary,
    breakdown,
    trend,
    hqProductivity,
    matrix,
    critical,
    volatility,
    creditNotes,
    isLoading,
    isError,
  } = useFieldSales({ activeTab });

  // Map each tab to its query result so a failed OPTIONAL section only shows an
  // error in its own tab — never blanks the whole page.
  const queryByKey = {
    summary,
    breakdown,
    trend,
    hqProductivity,
    matrix,
    critical,
    volatility,
    creditNotes,
  };

  const mon = monthTokenFromSummary(summary.data);
  const n = hqProductivity.data?.n || 20;
  const tabs = buildTabs(mon, n);

  // Summary TM total (sum of card4.tm_counts buckets) — used in the trend note.
  const tmTotal = Object.values(summary.data?.card4?.tm_counts ?? {}).reduce(
    (acc, v) => acc + (Number.isFinite(Number(v)) ? Number(v) : 0),
    0
  );

  return (
    <DashboardThemeProvider>
    <Box className="fs-page dk-root">
      <PageHeader subtitle="AI Field Sales" />

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Field Sales
      </Typography>

      <FieldSalesFilterBar />

      {isError ? (
        <ErrorState
          title="Failed to load field sales data."
          error={summary.error}
          onRetry={() => summary.refetch()}
          sx={{ mb: 2 }}
        />
      ) : null}

      {/* KPI cards + Auto Insights at PAGE level — visible on every tab. */}
      {isLoading ? (
        <KpiCardsSkeleton count={4} />
      ) : (
        <>
          <SectionErrorBoundary label="field-sales-kpis">
            <FieldSalesKpiCards summary={summary.data} />
          </SectionErrorBoundary>
          <SectionErrorBoundary label="field-sales-insights">
            <FieldSalesInsights insights={summary.data?.insights} card1={summary.data?.card1} pl={mon} />
          </SectionErrorBoundary>
        </>
      )}

      {isLoading ? null : (
        <SectionErrorBoundary label="field-sales-tabs">
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #eef1f6',
              borderRadius: '16px',
              p: 2,
              mb: 3,
              boxShadow: '0 1px 3px rgba(16,30,54,.06)',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_event, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                mb: 2,
                borderBottom: '1px solid #eef1f6',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: '#5b6472',
                  minHeight: 40,
                },
                '& .MuiTab-root.Mui-selected': { color: '#1657c8' },
                '& .MuiTabs-indicator': { backgroundColor: '#2E7CF6', height: 3 },
              }}
            >
              {tabs.map((t) => (
                <Tab key={t.label} label={t.label} />
              ))}
            </Tabs>

            {tabs.map((t, index) =>
              tab === index ? (
                <SectionErrorBoundary key={t.label} label={`field-sales-${t.queryKey}`}>
                  <TabPanel
                    label={t.label}
                    subtitle={t.subtitle}
                    query={queryByKey[t.queryKey]}
                    mon={mon}
                    tmTotal={tmTotal}
                  />
                </SectionErrorBoundary>
              ) : null
            )}
          </Box>
        </SectionErrorBoundary>
      )}
    </Box>
    </DashboardThemeProvider>
  );
}
